import { SurveyLogicRule, SurveyLogicRulesConfig, SurveyLogicScope } from '@vision/ui/interfaces';
import { v4 as uuidv4 } from 'uuid';

/**
 * Converts rules to condition_rules config format for API
 */
export function rulesToConditionRulesConfig(rules: SurveyLogicRule[]): SurveyLogicRulesConfig[] {
  return rules.map((rule) => ({
    id: rule.id,
    name: rule.name,
    action: {
      type: rule.action.type,
      scope: rule.action.scope,
      target_element_keys: rule.action.targetElementKeys || [],
      target_option_keys: (rule.action.targetOptionKeys || []).map((tok) => ({
        option_key: tok.optionKey,
        question_key: tok.questionKey,
      })),
      target_statement_keys: (rule.action.targetStatementKeys || []).map((tsk) => ({
        statement_key: tsk.statementKey,
        question_key: tsk.questionKey,
      })),
      target_events: rule.action.targetEvents || [],
    },
    condition_groups: (rule.conditionGroups || []).map((group) => ({
      id: group.id,
      operator: group.operator,
      conditions: (group.conditions || []).map((condition) => ({
        trigger_ref: condition.triggerRef
          ? {
              element_key: condition.triggerRef.elementKey,
              trigger_index: condition.triggerRef.triggerIndex,
            }
          : undefined,
        control_type: condition.controlType,
        rule_type: condition.ruleType,
        element_key: condition.elementKey,
        option_key: condition.optionKey,
        option_keys: condition.optionKeys,
        option_keys_operator: condition.optionKeysOperator,
        answer_value: condition.answerValue,
        statement_key: condition.statementKey,
        statement_keys: condition.statementKeys,
      })),
    })),
    condition_groups_operator: rule.conditionGroupsOperator,
  }));
}

/**
 * Parses condition_rules config from API back to ConditionRule format
 */
export function conditionRulesConfigToRules(config: SurveyLogicRulesConfig[]): SurveyLogicRule[] {
  return config.map((c) => ({
    id: c.id,
    name: c.name,
    action: {
      type: c.action.type,
      scope: c.action.scope,
      targetElementKeys: c.action.target_element_keys || [],
      targetOptionKeys: (c.action.target_option_keys || [])
        .filter((tok) => tok != null)
        .map((tok) => ({
          optionKey: tok.option_key || '',
          questionKey: tok.question_key || '',
        })),
      targetStatementKeys: (c.action.target_statement_keys || [])
        .filter((tsk) => tsk != null)
        .map((tsk) => ({
          statementKey: tsk.statement_key || '',
          questionKey: tsk.question_key || '',
        })),
      targetEvents: c.action.target_events || [],
    },
    conditionGroups: (c.condition_groups || []).map((group) => ({
      id: group.id,
      operator: group.operator,
      conditions: (group.conditions || []).map((condition) => ({
        id: uuidv4(),
        triggerRef: condition.trigger_ref
          ? {
              elementKey: condition.trigger_ref.element_key,
              triggerIndex: condition.trigger_ref.trigger_index,
            }
          : undefined,
        controlType: condition.control_type || 'option',
        ruleType: condition.rule_type || 'equals',
        elementKey: condition.element_key,
        optionKey: condition.option_key,
        optionKeys: condition.option_keys || [],
        optionKeysOperator: condition.option_keys_operator || 'and',
        answerValue: condition.answer_value,
        statementKey: condition.statement_key || '',
        statementKeys: condition.statement_keys || [],
      })),
    })),
    conditionGroupsOperator: c.condition_groups_operator,
  }));
}

/**
 * Builds the complete rules from condition_rules config
 */
export function buildRulesFromFlow(conditionRulesConfig?: SurveyLogicRulesConfig[]): SurveyLogicRule[] {
  if (conditionRulesConfig && conditionRulesConfig.length > 0) {
    return conditionRulesConfigToRules(conditionRulesConfig);
  }
  return [];
}

/**
 * Validation error with translation key and parameters
 */
export interface SurveyLogicValidationError {
  key: string;
  params?: Record<string, string | number>;
}

/**
 * Validates a rule has all required fields
 */
export function validateRule(
  rule: SurveyLogicRule,
  ruleIndex: number,
): { valid: boolean; errors: SurveyLogicValidationError[] } {
  const errors: SurveyLogicValidationError[] = [];

  // Check action has targets
  if (rule.action.scope === SurveyLogicScope.QUESTION && rule.action.targetElementKeys.length === 0) {
    errors.push({ key: 'validation.ruleNoTargetElement', params: { ruleIndex: ruleIndex + 1 } });
  }

  if (rule.action.scope === SurveyLogicScope.OPTION && rule.action.targetOptionKeys.length === 0) {
    errors.push({ key: 'validation.ruleNoTargetOption', params: { ruleIndex: ruleIndex + 1 } });
  }

  if (rule.action.scope === SurveyLogicScope.EVENT_DISPATCH && rule.action.targetEvents.length === 0) {
    errors.push({ key: 'validation.ruleNoTargetEvent', params: { ruleIndex: ruleIndex + 1 } });
  }

  // Check conditions
  rule.conditionGroups.forEach((group, groupIndex) => {
    group.conditions.forEach((condition, conditionIndex) => {
      if (!condition.elementKey && !condition.triggerRef?.elementKey) {
        errors.push({
          key: 'validation.conditionNoElement',
          params: { ruleIndex: ruleIndex + 1, groupIndex: groupIndex + 1, conditionIndex: conditionIndex + 1 },
        });
      }

      // Check for option selection - accept either optionKey (single) or optionKeys (multiple)
      const hasOptions = condition.optionKey || (condition.optionKeys && condition.optionKeys.length > 0);
      if (condition.controlType === 'option' && !hasOptions) {
        errors.push({
          key: 'validation.conditionNoOption',
          params: { ruleIndex: ruleIndex + 1, groupIndex: groupIndex + 1, conditionIndex: conditionIndex + 1 },
        });
      }

      if (condition.controlType === 'value' && !condition.answerValue) {
        errors.push({
          key: 'validation.conditionNoAnswerValue',
          params: { ruleIndex: ruleIndex + 1, groupIndex: groupIndex + 1, conditionIndex: conditionIndex + 1 },
        });
      }
    });
  });

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Validates all rules
 */
export function validateAllRules(rules: SurveyLogicRule[]): { valid: boolean; errors: SurveyLogicValidationError[] } {
  const allErrors: SurveyLogicValidationError[] = [];

  rules.forEach((rule, index) => {
    const { errors } = validateRule(rule, index);
    allErrors.push(...errors);
  });

  return {
    valid: allErrors.length === 0,
    errors: allErrors,
  };
}
